import React, { ReactNode, useMemo } from 'react';
import { Box, CssBaseline, useMediaQuery, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import AppHeader from './AppHeader';
import AppDrawer from './AppDrawer';
import { useAppKeyboardShortcuts } from '../../hooks/useAppKeyboardShortcuts';
import { useAuth } from '../../services/AuthContext';
import { getPermissions } from '../../utils/permissions';

interface MainLayoutProps {
  open: boolean;
  toggleDrawer: () => void;
  drawerWidth: number;
  closedWidth: number;
  menuItems: {
    text: string;
    icon: React.ReactNode;
    path: string;
  }[];
  children?: ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = React.memo(({ 
  open, 
  toggleDrawer, 
  drawerWidth, 
  closedWidth, 
  menuItems,
  children 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { role } = useAuth();
  const canEditServiceCalls = useMemo(
    () => getPermissions(role).canEditServiceCalls,
    [role],
  );
  useAppKeyboardShortcuts(true, { canEditServiceCalls });

  const mainContentStyles = {
    flexGrow: 1,
    mt: { xs: 7, sm: 8 }, // Smaller top margin on mobile
    px: {
      xs: '8px',
      sm: '16px',
      md: '16px',
      lg: 'clamp(16px, 10vw, 96px)',
    },
    pb: { xs: 2, sm: 3 }, // Add bottom padding
    maxWidth: '1400px',
    mx: 'auto',
    width: isMobile ? '100%' : `calc(100% - ${open ? drawerWidth : closedWidth}px)`,
    transition: (theme: Theme) => theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflow: 'auto', // Enable scrolling
    minHeight: 0, // Allow flex child to shrink
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      <AppDrawer 
        open={open} 
        toggleDrawer={toggleDrawer} 
        drawerWidth={drawerWidth} 
        closedWidth={closedWidth} 
        menuItems={menuItems} 
      />
      
      <AppHeader 
        open={open} 
        toggleDrawer={toggleDrawer} 
      />
      
      <Box 
        component="main" 
        sx={mainContentStyles}
      >
        {children}
      </Box>
    </Box>
  );
});

MainLayout.displayName = 'MainLayout';

export default MainLayout;